// External Libraries
import * as React from 'react';
import { Box, Button, Container, Link, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

// Icons
import { AddToQueueOutlined, Assessment, Login } from '@mui/icons-material';

// Local Components
import Subscribe from '../Subscribe';

// Hooks
import useLoggedIn from '../../hooks/useLoggedIn';
import useRegionDetection from '../../hooks/useRegionDetection';

export default function Hero() {
  const { loggedIn } = useLoggedIn();
  
  // Use the custom hook to detect if user is from the UK
  const isUserUK = useRegionDetection();

  return (
    <React.Fragment>
      <Box
        sx={{
          bgcolor: 'background.paper', // Revert to paper background
          pt: 6, // Keep increased padding
          pb: 6,
          mt: 2, // Add back some top margin for separation
          mb: 4, // Keep bottom margin
        }}
      >
        <Container maxWidth="md" component="main"> {/* Use md for better centering on larger screens */}
          <Typography
            component="h1"
            variant="h2"
            align="center"
            color="text.primary"
            gutterBottom
            sx={{
              fontSize: {
                lg: '3.5rem', // Slightly adjust font sizes
                md: '3rem',
                sm: '2.5rem',
                xs: '2rem'
              },
              mb: 3 // Add margin below title
            }}
          >
            Perfect Exam Screens in <span style={{ fontWeight: 700, color: '#2ead4b' }}>Seconds</span> {/* Use theme color */}
          </Typography>
          {/* Center the video box */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: 4 // Add margin below video
            }}
          >
            {/* Ensure this Box has the size and position styling for the iframe container */}
            <Box
              sx={{
                width: '100%',
                maxWidth: 800,
                height: 450,
                position: 'relative',
              }}
            >
              <Box
                component="div"
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                }}
                dangerouslySetInnerHTML={{
                  __html: isUserUK
                    ? `<div class="wistia_responsive_padding" style="padding:56.25% 0 0 0;position:relative;"><div class="wistia_responsive_wrapper" style="height:100%;left:0;position:absolute;top:0;width:100%;"><iframe src="https://fast.wistia.net/embed/iframe/x8mspf55y6?web_component=true&seo=true" title="Marketing 2025 - UK Video" allow="autoplay; fullscreen" allowtransparency="true" frameborder="0" scrolling="no" class="wistia_embed" name="wistia_embed" width="100%" height="100%"></iframe></div></div><script src="https://fast.wistia.net/player.js" async></script>`
                    : `<div class="wistia_responsive_padding" style="padding:56.25% 0 0 0;position:relative;"><div class="wistia_responsive_wrapper" style="height:100%;left:0;position:absolute;top:0;width:100%;"><iframe src="https://fast.wistia.net/embed/iframe/wp2hawq56s?web_component=true&seo=true" title="Marketing 2025 - International Video" allow="autoplay; fullscreen" allowtransparency="true" frameborder="0" scrolling="no" class="wistia_embed" name="wistia_embed" width="100%" height="100%"></iframe></div></div><script src="https://fast.wistia.net/player.js" async></script>`
                }}
              />
            </Box>
            {/* Removed the extra nested Box and incorrect closing tag placement */}
          </Box>
          {loggedIn ? (
            <Button
              variant="contained"
              fullWidth
              color="secondary" // Keep secondary for logged in
              size="large" // Make button larger
              sx={{ mt: 3 }} // Adjust margin
              startIcon={<AddToQueueOutlined />} // Default size icon
            >
              <Link sx={{ textDecoration: "none", color: 'inherit' }} component={RouterLink} to={`/invigilation`}>Create Exam Displays</Link> {/* Inherit color */}
            </Button>
          ) : (
            <>
              <Stack
                sx={{ pt: 3, mb: 2 }} // Adjust padding and margin
                spacing={3}
                direction="row"
                justifyContent="center" // Keep centered
                alignItems="center" // Align items vertically
              >
                {/* Make Subscribe the primary CTA */}
                <Subscribe color="success" size="large" />
                {/* Use outlined buttons for secondary actions */}
                <Button
                  variant="outlined" // Change to outlined
                  color="info"
                  size="large" // Consistent size
                  startIcon={<Assessment />} // Default size icon
                  sx={{ ml: 2 }} // Add margin left
                >
                  <Link sx={{ textDecoration: "none", color: "info.main" }} component={RouterLink} to={`/trial`}>Request Trial</Link> {/* Use theme color */}
                </Button>
                <Button
                  variant="outlined" // Change to outlined
                  color="primary" // Use primary color for sign in
                  size="large" // Consistent size
                  startIcon={<Login />} // Default size icon
                  sx={{ ml: 2 }} // Add margin left
                >
                  <Link sx={{ textDecoration: "none", color: "primary.main" }} component={RouterLink} to={`/signin`}>Sign In</Link> {/* Use theme color */}
                </Button>
              </Stack>
              <Typography
                variant="caption"
                align="left"
                color="secondary"
              >
                Need a different payment method, or purchase order? <Link sx={{ textDecoration: "none", color: "tertiary.main" }} component={RouterLink} to={`/contact`}>Contact us</Link>
              </Typography>
            </>
          )}
        </Container>
      </Box>
    </React.Fragment>
  );
}
